/**
 * PrintModeQuestion (BLU-1376, D2): the full-focus "How do you print?" card.
 *
 * A new org (setup-required, no saved answer) sees only this card on
 * /onboarding. The answer picks the wizard's step list: `client` = a 3PL
 * prints for the org (client steps), `self` = the org prints on its own floor
 * (owner steps).
 *
 * `mode="change"` is the band's Change link: the saved answer is preselected,
 * an eyebrow reads "Change your answer", the footer says finished steps stay
 * finished, and the actions are "Keep my answer" (no save, calls `onDone`)
 * and "Save" (saves a different pick, then calls `onDone`).
 *
 * Save path: `onboarding.setPrintMode`, then the cached
 * `productSync.getOnboardingStatus` is patched from the mutation's own return
 * (cancel any in-flight poll first so it cannot overwrite the patch). No
 * invalidate and no refetch: the answer changes no server signal, only the
 * client-side step list.
 *
 * RBAC: the UI gate (`org_settings.read`) agrees with the server gate
 * (`requirePermission(OrgSettingsRead)`); without it the card is read-only and
 * says why.
 */
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PRINT_MODES, type PrintMode } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { useRole } from '@/hooks/use-role';
import { OptionCard } from './shell/OptionCard';

export interface PrintModeQuestionProps {
  /** `first` = the new org's question; `change` = changing a saved answer. */
  mode?: 'first' | 'change';
  /** The saved answer, preselected in change mode. */
  current?: PrintMode | null;
  /** Change mode: called after "Keep my answer" or a successful "Save". */
  onDone?: () => void;
}

export function PrintModeQuestion({ mode = 'first', current = null, onDone }: PrintModeQuestionProps) {
  const changing = mode === 'change';
  const { t } = useTranslation('onboarding');
  const { can } = useRole();
  const canSave = can('org_settings.read');
  const utils = trpc.useUtils();
  const headingId = useId();
  const groupName = useId();
  const [selected, setSelected] = useState<PrintMode | null>(changing ? current : null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const mutation = trpc.onboarding.setPrintMode.useMutation({
    retry: false,
    onSuccess: async (result) => {
      await utils.productSync.getOnboardingStatus.cancel();
      utils.productSync.getOnboardingStatus.setData(undefined, (prev) =>
        prev ? { ...prev, printMode: result.printMode } : prev,
      );
      if (changing) onDone?.();
    },
    onError: () => {
      const message = t('printMode.saveError');
      setSaveError(message);
      toast.error(message);
    },
  });

  const handleContinue = () => {
    if (!selected || !canSave || mutation.isPending || (changing && selected === current)) return;
    setSaveError(null);
    mutation.mutate({ printMode: selected });
  };

  const isPending = mutation.isPending;
  // Change mode saves only a different answer; the same answer is "Keep my answer".
  const unchanged = changing && selected === current;

  return (
    <section className="mx-auto w-full max-w-2xl rounded-xl border bg-card p-6 shadow-sm sm:p-8" aria-labelledby={headingId}>
      {changing && (
        <p className="mb-1 text-[11px] font-semibold uppercase leading-4 tracking-wide text-muted-foreground">
          {t('printMode.changeEyebrow')}
        </p>
      )}
      <h2 id={headingId} className="text-xl font-semibold tracking-tight text-foreground">
        {t('printMode.title')}
      </h2>
      <p className="mt-1.5 text-sm text-muted-foreground">{t('printMode.subtitle')}</p>

      <div role="radiogroup" aria-labelledby={headingId} className="mt-6 grid gap-3 sm:grid-cols-2">
        {PRINT_MODES.map((mode) => (
          <OptionCard
            key={mode}
            name={groupName}
            value={mode}
            checked={selected === mode}
            onChange={(value) => {
              setSelected(value as PrintMode);
              setSaveError(null);
            }}
            label={t(`printMode.${mode}.label`)}
            body={t(`printMode.${mode}.body`)}
            disabled={!canSave || isPending}
          />
        ))}
      </div>

      {!canSave && (
        <p className="mt-4 text-sm text-muted-foreground">{t('printMode.noAccess')}</p>
      )}

      {saveError && (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {saveError}
        </p>
      )}

      <div className="mt-6 flex flex-col-reverse gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">{t(changing ? 'printMode.changeNote' : 'printMode.footer')}</p>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {changing && (
            <Button type="button" variant="outline" onClick={onDone} disabled={isPending}>
              {t('printMode.keep')}
            </Button>
          )}
          <Button
            type="button"
            onClick={handleContinue}
            disabled={!selected || !canSave || isPending || unchanged}
            className="shrink-0 gap-1.5 bg-primary-strong text-white hover:bg-primary-strong/90"
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {t(changing ? 'printMode.save' : 'printMode.continue')}
          </Button>
        </div>
      </div>
    </section>
  );
}
